import React, {
  createContext,
  useContext,
  useState,
  FC,
  PropsWithChildren,
  useEffect,
} from 'react';
import { useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import palette from '~/theme/palette';
import { draftIdPostfix } from '~/hooks/draft';

export type EditMode = 'VIEW' | 'DRAFT' | 'CHANGELOG' | 'LOCK_EDIT';

interface EditModeContextProps {
  editMode: EditMode;
  setEditMode: (state: EditMode) => void;
}

const UseEditModeContext = createContext<EditModeContextProps>({
  editMode: 'VIEW',
  setEditMode: () => {},
});

export const EditModeProvider: FC<PropsWithChildren<{}>> = ({ children }) => {
  const [editMode, setEditMode] = useState<EditMode>('VIEW');

  const location = useLocation();
  const currentPathname = location.pathname;

  useEffect(() => {
    if (!currentPathname.includes('/show/profile')) {
      setEditMode('VIEW');
    } else if (currentPathname.includes(draftIdPostfix) && editMode !== 'LOCK_EDIT') {
      setEditMode('DRAFT');
    } else if (editMode !== 'CHANGELOG') {
      setEditMode('VIEW');
    }
  }, [currentPathname]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <UseEditModeContext.Provider value={{ editMode, setEditMode }}>
      {children}
    </UseEditModeContext.Provider>
  );
};

export const useEditMode = (): EditModeContextProps => {
  return useContext(UseEditModeContext);
};

const EDIT_MODE_COLORS: Partial<Record<EditMode, { base: string; hint: string }>> = {
  CHANGELOG: { base: palette.changelog.base, hint: palette.warning.main },
  DRAFT: { base: palette.draft.base, hint: palette.success.main },
  LOCK_EDIT: { base: palette.lockEdit.base, hint: palette.error.main },
};

export const getBaseEditModeColor = (editMode: EditMode) => EDIT_MODE_COLORS[editMode]?.base;
export const getHintEditModeColor = (editMode: EditMode) => EDIT_MODE_COLORS[editMode]?.hint;

export const useEditModeStyles = makeStyles(() => ({
  backgroundColor: ({ editMode }: { editMode: EditMode }) => ({
    backgroundColor: getBaseEditModeColor(editMode),
    // border: `10px solid ${getBaseEditModeColor(editMode)}`,
  }),
}));
